import { Expose, Transform, Type } from 'class-transformer';
import {
	dateTransformer,
	temporalPlainDateTransformer,
	temporalZonedTransformer,
} from 'src/lib/utilities/api/class-transformer-types';
import {
	PagingSearchFieldArgument,
	PagingSearchFieldArrayArgument,
} from 'src/lib/utilities/api/patterns/pagination/paging-search-field.argument';

export class SupportingPaperworkModel {
	name: string;
	url: string;
}

export function SupportingPaperworkModel_ClassTransform_Type() {
	return SupportingPaperworkModel;
}

export class AssociatedPaperworkModel {
	id: number;
	file_url: string;
	completed_file_url: string;
	@Type(SupportingPaperworkModel_ClassTransform_Type)
	supporting_files: SupportingPaperworkModel[];
	title: string;
	type_title: string;
	tags: string[];
}

export function AssociatedPaperworkModel_ClassTransform_Type() {
	return AssociatedPaperworkModel;
}

export class PaperworkApprovalRequestorModel {
	uid: number;
	name: string;
}

export function PaperworkApprovalRequestor_ClassTransform_Type() {
	return PaperworkApprovalRequestorModel;
}

export class ApprovalSupportingPaperworkModel {
	file_id: number;
	url: string;

	get name(): string | null {
		const raw = this.url?.split('/').pop() ?? null;
		return raw != null ? decodeURIComponent(raw) : null;
	}
}

export function ApprovalSupportingPaperworkModel_ClassTransform_Type() {
	return ApprovalSupportingPaperworkModel;
}

export class PaperworkApprovalMissingChecklistItemModel {
	can_waive: boolean;
	requires_file: boolean;
	title: string;
	type: string;
	type_title: string;
}

export function PaperworkApprovalMissingChecklistItemModel_ClassTransform_Type() {
	return PaperworkApprovalMissingChecklistItemModel;
}

export class PaperworkApprovalModel {
	ai_scan_details: string;
	@Expose({ name: 'created_timestamp' })
	@Transform(temporalZonedTransformer)
	created_temporal: Temporal.ZonedDateTime;
	enrollment_status: number;
	@Transform(dateTransformer)
	expiration_date: Date;
	@Expose({ name: 'expiration_timestamp' })
	@Transform(temporalZonedTransformer)
	expiration_temporal: Temporal.ZonedDateTime;
	file_id: number;
	id: number;
	institute_id: number;
	institute_state: string;
	institute_title: string;
	institute_type: string;
	internal_comment: string;
	@Type(PaperworkApprovalMissingChecklistItemModel_ClassTransform_Type)
	missing_checklist_items: PaperworkApprovalMissingChecklistItemModel[];
	name: string;
	paperwork_type: string;
	parent_institute_id: number;
	parent_institute_title: string;
	public_comment: string;
	@Type(PaperworkApprovalRequestor_ClassTransform_Type)
	requestor: PaperworkApprovalRequestorModel;
	source_item_id: string;
	source_request_id: string;
	source_title: string;
	source_type: string;
	status: string;
	@Type(PaperworkApprovalRequestor_ClassTransform_Type)
	status_decided_by: PaperworkApprovalRequestorModel;
	@Expose({ name: 'status_decided_on_timestamp' })
	@Transform(temporalZonedTransformer)
	status_decided_on_temporal: Temporal.ZonedDateTime;
	status_decided_reason: string;
	@Transform(temporalPlainDateTransformer)
	submission_date: Temporal.PlainDate;
	@Expose({ name: 'submission_timestamp' })
	@Transform(temporalZonedTransformer)
	submission_temporal: Temporal.ZonedDateTime;
	@Type(ApprovalSupportingPaperworkModel_ClassTransform_Type)
	supporting_files: ApprovalSupportingPaperworkModel[];
	tags: string[];
	uid: number;
	@Expose({ name: 'updated_timestamp' })
	@Transform(temporalZonedTransformer)
	updated_temporal: Temporal.ZonedDateTime;
	url: string;

	get fileName(): string | null {
		const raw = this.url?.split('/').pop() ?? null;
		return raw != null ? decodeURIComponent(raw) : null;
	}
}

export function PaperworkApprovalModel_ClassTransform_Type() {
	return PaperworkApprovalModel;
}

export class PaperworkApprovalTrackerModel {
	count: number;
	@Type(PaperworkApprovalModel_ClassTransform_Type)
	results: PaperworkApprovalModel[];
}

export interface PaperworkApprovalTrackerArgument {
	created_date: PagingSearchFieldArgument;
	enrollment_status: PagingSearchFieldArrayArgument;
	institute_state: PagingSearchFieldArrayArgument;
	institute_title: PagingSearchFieldArgument;
	institute_type: PagingSearchFieldArrayArgument;
	missing_checklist_items: PagingSearchFieldArgument;
	name: PagingSearchFieldArgument;
	paperwork_type: PagingSearchFieldArgument;
	requestor_name: PagingSearchFieldArgument;
	source_type: PagingSearchFieldArrayArgument;
	source_request_id: PagingSearchFieldArgument;
	submission_date: PagingSearchFieldArgument;
	uid: PagingSearchFieldArgument;
}

export interface PaperworkArgument {
	item_id: number;
	comment: string;
	tags?: string[];
}

// old arg, keeping for old call in case for now
export interface PaperworkApproveRejectArgument {
	paperwork_request_id: number;
	paperwork_items_to_approve: PaperworkArgument[];
	paperwork_items_to_reject: PaperworkArgument[];
}

export interface PaperworkApprovalSharedArgument {
	approved: boolean;
	expiration_timestamp: number;
	paperwork_type: string;
	tags: string[];
}

export interface SupportingFileArgument extends PaperworkApprovalSharedArgument {
	file_id: number;
}

export interface PaperworkApprovalArgument extends PaperworkApprovalSharedArgument {
	pending_paperwork_id: number;
	keep_base_file: boolean;
	public_comment: string;
	internal_comment: string;
	supporting_files: SupportingFileArgument[];
}
